/**
 * Login — email + password form.
 *
 * Behaviour:
 *   - Validates both fields are present client-side (browser handles
 *     email format via type="email").
 *   - Calls signIn() from AuthContext. On success, AuthContext updates,
 *     and PublicOnly/RoleGuard in App.jsx redirects to the user's
 *     correct dashboard automatically — no manual navigate() needed.
 *   - On failure, displays the Supabase error message inline (we
 *     normalise the most common ones to friendlier text).
 *   - "Forgot password?" opens an inline notice instead of an email
 *     flow — per BIT 11, the Super Admin generates temp passwords.
 *   - Show/hide password toggle via the eye icon.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import AuthLayout from './AuthLayout.jsx';
import TextInput from '../../components/ui/TextInput.jsx';
import Icon from '../../components/ui/Icons.jsx';

function friendlyAuthError(message) {
  if (!message) return 'Something went wrong. Please try again.';
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials') || m.includes('invalid email or password')) {
    return 'Email or password is incorrect.';
  }
  if (m.includes('email not confirmed')) {
    return 'This email has not been confirmed yet. Contact your Super Admin.';
  }
  if (m.includes('user not found')) {
    return 'No account found with that email.';
  }
  if (m.includes('too many requests') || m.includes('rate')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  return message;
}

export default function Login() {
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setSubmitting(true);
    const { error: authError } = await signIn(trimmedEmail, password);
    setSubmitting(false);

    if (authError) {
      setError(friendlyAuthError(authError.message));
      // Clear password on auth failure for safety; keep email for retry.
      setPassword('');
    }
    // On success: AuthContext updates session; App.jsx's PublicOnly
    // wrapper sees the new session and redirects automatically.
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your ORIN account"
      footer={
        <>
          Don&apos;t have an account?{' '}
          <Link
            to="/register"
            className="font-semibold hover:underline"
            style={{ color: 'var(--accent)' }}
          >
            Register
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <TextInput
          id="email"
          type="email"
          label="Email"
          placeholder="you@entrenouseventcreators.com"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
        />

        <TextInput
          id="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          placeholder="••••••••"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
          rightAction={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="text-muted hover:text-ink transition-colors p-1 -m-1"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
            </button>
          }
        />

        <div className="text-right -mt-1">
          <button
            type="button"
            onClick={() => setForgotOpen((v) => !v)}
            className="text-xs font-medium text-muted hover:text-ink transition-colors"
          >
            Forgot password?
          </button>
        </div>

        {forgotOpen && (
          <div
            className="text-xs text-muted bg-slate-50 border border-border-base rounded-md p-3"
            role="note"
          >
            Password resets are handled by your Super Admin. Contact them
            directly to receive a temporary password.
          </div>
        )}

        {error && (
          <div
            className="text-sm text-status-overdue bg-red-50 border border-red-200 rounded-md px-3 py-2"
            role="alert"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary w-full justify-center mt-2 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthLayout>
  );
}